import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { CycleProvider } from '@/lib/cycle';
import { Toaster } from 'sonner';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import UploadCenter from '@/pages/UploadCenter';
import Production from '@/pages/Production';
import Maintenance from '@/pages/Maintenance';
import NPD from '@/pages/NPD';
import BusinessSales from '@/pages/manual/BusinessSales';
import Quality from '@/pages/manual/Quality';
import SupplyChain from '@/pages/manual/SupplyChain';
import HRSafety from '@/pages/manual/HRSafety';
import Environment from '@/pages/manual/Environment';
import ChangePoints from '@/pages/manual/ChangePoints';
import ActionTracker from '@/pages/ActionTracker';
import GeneratePPT from '@/pages/GeneratePPT';
import Settings from '@/pages/Settings';
import CustomerMaster from '@/pages/CustomerMaster';
import ScheduleVsSupply from '@/pages/ScheduleVsSupply';
import { useAccess } from '@/lib/access';
import { Empty } from '@/components/UI';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[var(--aampl-bg)]"><div className="spinner h-10 w-10"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Section pages are reachable only when the user holds an owning section; roles gate admin pages.
function RequireAccess({ sections, roles, children }) {
  const { user, can } = useAccess();
  const roleOk = !roles || roles.includes(user?.role);
  const sectionOk = !sections || can(...sections);
  if (!roleOk || !sectionOk) {
    return <Empty title="Access restricted" hint="This section is not part of your assigned access. Ask the administrator to add it under User Management." />;
  }
  return children;
}

function Shell() {
  return (
    <CycleProvider>
      <Layout />
    </CycleProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedRoute><Shell /></ProtectedRoute>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/uploads" element={<UploadCenter />} />
            <Route path="/production" element={<RequireAccess sections={['production']}><Production /></RequireAccess>} />
            <Route path="/maintenance" element={<RequireAccess sections={['maintenance']}><Maintenance /></RequireAccess>} />
            <Route path="/npd" element={<RequireAccess sections={['npd']}><NPD /></RequireAccess>} />
            <Route path="/business-sales" element={<RequireAccess sections={['business_sales']}><BusinessSales /></RequireAccess>} />
            <Route path="/quality" element={<RequireAccess sections={['quality']}><Quality /></RequireAccess>} />
            <Route path="/supply-chain" element={<RequireAccess sections={['supply_chain']}><SupplyChain /></RequireAccess>} />
            <Route path="/hr-safety" element={<RequireAccess sections={['hr_safety', 'hr']}><HRSafety /></RequireAccess>} />
            <Route path="/environment" element={<RequireAccess sections={['environment']}><Environment /></RequireAccess>} />
            <Route path="/change-points" element={<RequireAccess sections={['quality']}><ChangePoints /></RequireAccess>} />
            <Route path="/actions" element={<ActionTracker />} />
            <Route path="/customers" element={<CustomerMaster />} />
            <Route path="/supply-chain/schedule-vs-supply" element={<RequireAccess sections={['schedule_vs_supply']}><ScheduleVsSupply /></RequireAccess>} />
            <Route path="/generate-ppt" element={<RequireAccess roles={['admin', 'management']}><GeneratePPT /></RequireAccess>} />
            <Route path="/settings" element={<RequireAccess roles={['admin']}><Settings /></RequireAccess>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
